import {
  Button,
  Checkbox,
  Fieldset,
  Group,
  NumberInput,
  ScrollArea,
  Select,
  Switch,
  Text,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { intercultureApi } from '../../data-access/apis/interculture';
import { intercultureQueries } from '../../data-access/queries/interculture';
import {
  IntercultureCrop,
  IntercultureFormComplete,
} from '../../data-access/types/types';

import {
  formatDate,
  getDirtyValues,
  useQueriesWithGlobal,
  VarietyRel,
} from '@data-access';
import { Drawer } from '@ui';

const isSowingDisabled = () => {
  const today = dayjs();
  const startDate = dayjs(`${today.year()}-03-01`);
  const endDate = dayjs(`${today.year()}-08-25`);
  return today.isBetween(startDate, endDate, null, '[]'); // '[]' includes the start and end dates
};

const isHarvestDisabled = () => {
  const today = dayjs();
  const startDate = dayjs(`${today.year()}-08-26`);
  const endDate = dayjs(`${today.year()}-12-31`);
  return today.isBetween(startDate, endDate, null, '[]'); // '[]' includes the start and end dates
};

interface IntercultureFormProps {
  opened: boolean;
  selectedType: string;
  onClose: () => void;
  selectedForm?: number;
  selectedForms?: number[];
}

type FormValues = Omit<
  Partial<IntercultureFormComplete>,
  'harvest_date' | 'seed_date' | 'previous_crop_harvest_date'
> & {
  harvest_date?: Date | null;
  seed_date?: Date | null;
  previous_crop_harvest_date?: Date | null;
};

export const IntercultureForm: React.FC<IntercultureFormProps> = ({
  opened,
  onClose,
  selectedForm,
  selectedForms,
  selectedType,
}) => {
  const { t } = useTranslation();
  const form = useForm<FormValues>({});

  const queries = useQueriesWithGlobal(intercultureQueries);
  const values = useQuery(queries.values({ crop: selectedType }));
  const intercultureForm = useQuery({
    ...queries.result({
      crop: selectedType,
      id: selectedForm!,
    }),
    enabled: selectedForm !== undefined,
  });

  const updateForm = useMutation({
    mutationFn: intercultureApi.updateForm,
  });
  const updateManyForms = useMutation({
    mutationFn: intercultureApi.updateManyForms,
  });

  const handleSubmit = async () => {
    const dirtyValues = getDirtyValues(form);

    if (Object.keys(dirtyValues).length === 0) {
      return;
    }

    const { seed_date, harvest_date, previous_crop_harvest_date, ...rest } =
      dirtyValues;

    const transformedValues: Partial<IntercultureFormComplete> = {
      ...rest,
    };
    if (seed_date) {
      transformedValues.seed_date = formatDate({
        date: seed_date,
        format: 'db',
      });
    }
    if (harvest_date) {
      transformedValues.harvest_date = formatDate({
        date: harvest_date,
        format: 'db',
      });
    }
    if (previous_crop_harvest_date) {
      transformedValues.previous_crop_harvest_date = formatDate({
        date: previous_crop_harvest_date,
        format: 'db',
      });
    }

    if (selectedForm) {
      updateForm.mutate({
        crop: selectedType,
        id: selectedForm,
        data: transformedValues,
      });
    } else if (selectedForms) {
      updateManyForms.mutate({
        crop: selectedType,
        id: selectedForms,
        data: transformedValues,
      });
    }
  };

  useEffect(() => {
    if (intercultureForm.data) {
      const { harvest_date, seed_date, previous_crop_harvest_date } =
        intercultureForm.data;
      form.setInitialValues({
        ...intercultureForm.data,
        variety:
          intercultureForm.data.variety?.id.toString() as unknown as VarietyRel,
        previous_crop:
          intercultureForm.data.previous_crop?.informed?.id.toString() as unknown as IntercultureCrop,
        harvest_date: harvest_date ? dayjs(harvest_date).toDate() : undefined,
        seed_date: seed_date ? dayjs(seed_date).toDate() : undefined,
        previous_crop_harvest_date: previous_crop_harvest_date
          ? dayjs(previous_crop_harvest_date).toDate()
          : undefined,
      });
      form.reset();
    }
  }, [intercultureForm.data]);

  useEffect(() => {
    if (updateForm.isSuccess || updateManyForms.isSuccess) {
      onClose();
      updateForm.reset();
      updateManyForms.reset();
    }
  }, [updateForm.isSuccess, updateManyForms.isSuccess]);

  return (
    <Drawer
      closeOnClickOutside={false}
      onClose={onClose}
      opened={opened}
      position="right"
      scrollAreaComponent={ScrollArea.Autosize}
      size="auto"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Fieldset
          legend={t('interculture.sowing_fieldset_legend')}
          variant="filled"
        >
          {isSowingDisabled() ? (
            <Text c="red" maw={300} mb="sm">
              {t('interculture.sowing_date_alert')}
            </Text>
          ) : null}
          <Checkbox
            label={t('common.properties..is_seeded')}
            {...form.getInputProps('is_seeded', { type: 'checkbox' })}
          />
          <Fieldset disabled={!form.values.is_seeded} variant="unstyled">
            <Select
              data={values.data?.options.variety}
              label={t('table.columns.intercultureVariety')}
              mt="sm"
              {...form.getInputProps('variety')}
            />
            <DateInput
              defaultDate={dayjs(values.data?.raw.seed_date).toDate()}
              // key is needed to ensure that date input will be re-rendered to set default date
              key={dayjs(values.data?.raw.seed_date).toDate().toString()}
              label={t('table.columns.intercultureSeedDate')}
              mt="sm"
              {...form.getInputProps('seed_date')}
            />
            <Checkbox
            label={t('common.properties.is_irrigated')}
            mt="sm"
            {...form.getInputProps('is_irrigated', { type: 'checkbox' })}
          />
          <Select
            data={values.data?.options.previous_crop}
            label={t('common.previous_crop')}
            mt="sm"
            {...form.getInputProps('previous_crop')}
          />
          <DateInput
            defaultDate={dayjs(
              values.data?.raw.previous_crop_harvest_date,
            ).toDate()}
            // key is needed to ensure that date input will be re-rendered to set default date
            key={dayjs(values.data?.raw.previous_crop_harvest_date).format(
              'YYYY-MM-DD',
            )}
            label={t('common.previous_crop_harvest_date')}
            mt="sm"
            {...form.getInputProps('previous_crop_harvest_date')}
          />
          </Fieldset>
          
        </Fieldset>

        <Fieldset
          legend={t('interculture.harvest_fieldset_legend')}
          mt="md"
          variant="filled"
        >
          {isHarvestDisabled() ? (
            <Text c="red" maw={300} mb="sm">
              {t('interculture.harvest_date_alert')}
            </Text>
          ) : null}
          <Checkbox
            label={t('common.is_harvested')}
            {...form.getInputProps('is_harvested', { type: 'checkbox' })}
          />
          <Fieldset disabled={!form.values.is_harvested} variant="unstyled">
            <DateInput
              defaultDate={dayjs(values.data?.raw.harvest_date).toDate()}
              // key is needed to ensure that date input will be re-rendered to set default date
              key={dayjs(values.data?.raw.harvest_date).toDate().toString()}
              label={t('common.properties.harvest_date')}
              mt="sm"
              {...form.getInputProps('harvest_date')}
            />
            {/* <Switch
              label={t('common.properties.is_farm_yield')}
              mt="sm"
              {...form.getInputProps('is_farm_yield', { type: 'checkbox' })}
            /> */}
            {/* {form.values.is_farm_yield ? ( */}
            <NumberInput
              label={t('common.properties.farm_yield_tons')}
              mt="sm"
              {...form.getInputProps('farm_yield_tons')}
            />
          {/* ) : ( */}
            <NumberInput
              label={t('common.properties.informed_yield')}
              mt="sm"
              {...form.getInputProps('informed_yield')}
            />
            {/* )} */}
          </Fieldset>
        </Fieldset>

        {/* <Fieldset legend="Paramètres du conseil" mt="md" variant="filled">
          <Checkbox label="Masquer le conseil pour l'utilisateur" />
          <Checkbox label="Ne pas envoyer le récapitulatif par mail" mt="sm" />
          <Checkbox label="Relancer le conseil" mt="sm" />
          <Select data={[]} label="Source d'image" mt="sm" />
        </Fieldset> */}

        <Group mt="md">
          <Button
            loading={updateForm.isPending || updateManyForms.isPending}
            type="submit"
          >
            {t('common.save')}
          </Button>
          <Button color="red" onClick={onClose} type="button">
            {t('common.previous')}
          </Button>
        </Group>
      </form>
    </Drawer>
  );
};
